import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Pagination,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { flowApi } from '../../services/api';

const MyFlowsModal = ({ open, onClose, onLoadFlow }) => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    if (open) {
      fetchFlows();
    }
  }, [open, searchQuery, selectedTags, sortBy, sortOrder, page]);

  const fetchFlows = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const params = {
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      };

      if (searchQuery.trim()) {
        params.search_query = searchQuery.trim();
      }

      if (selectedTags.length > 0) {
        params.tag_filter = selectedTags.join(',');
      }

      const data = await flowApi.getMyFlows(params);
      setFlows(data.flows || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count || 0);

    } catch (err) {
      setError(err.message || 'Failed to load your flows');
      setFlows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFlow = async (flowId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/v1/flows/${flowId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete flow');
      }

      // Refresh the flows list
      fetchFlows();
      setAnchorEl(null);
      setSelectedFlow(null);

    } catch (err) {
      setError(err.message || 'Failed to delete flow');
    }
  };

  const handleLoadFlow = async (flow) => {
    try {
      // Record usage
      const token = localStorage.getItem('access_token');
      if (token) {
        await flowApi.recordUsage(flow.flow_id);
      }

      // Load the flow
      if (onLoadFlow) {
        onLoadFlow(flow);
      }

      onClose();

    } catch (err) {
      // Don't show error for usage tracking failure
      if (onLoadFlow) {
        onLoadFlow(flow);
      }
      onClose();
    }
  };

  const handleMenuClick = (event, flow) => {
    setAnchorEl(event.currentTarget);
    setSelectedFlow(flow);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFlow(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          My Saved Flows
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {/* Search and Filter */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search your flows..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {totalCount} flow{totalCount !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : flows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? 'No flows match your search.' : 'You haven\'t saved any flows yet.'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Flows List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {flows.map((flow) => (
                <FlowCard
                  key={flow.flow_id}
                  flow={flow}
                  onLoad={() => handleLoadFlow(flow)}
                  onMenuClick={(event) => handleMenuClick(event, flow)}
                />
              ))}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleLoadFlow(selectedFlow)}>
          <PlayIcon sx={{ mr: 1 }} />
          Load Flow
        </MenuItem>
        <MenuItem onClick={() => handleDeleteFlow(selectedFlow?.flow_id)}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

const FlowCard = ({ flow, onLoad, onMenuClick }) => {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {flow.original_query}
            </Typography>
            
            {flow.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {flow.description}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {flow.is_public ? (
              <Tooltip title="Public flow">
                <PublicIcon color="primary" fontSize="small" />
              </Tooltip>
            ) : (
              <Tooltip title="Private flow">
                <PrivateIcon color="action" fontSize="small" />
              </Tooltip>
            )}
            
            <IconButton size="small" onClick={onMenuClick}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {flow.tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 14 }} color="action" />
              <Typography variant="caption" color="text.secondary">
                {new Date(flow.created_at).toLocaleDateString()}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon sx={{ fontSize: 14 }} color="action" />
              <Typography variant="caption" color="text.secondary">
                {flow.usage_count} uses
              </Typography>
            </Box>
          </Box>

          <Button
            size="small"
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={onLoad}
            sx={{ borderRadius: 2 }}
          >
            Load
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MyFlowsModal;
